import React, { useEffect, useState, useRef, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { viewLeadById, updateLead, listLocalityByCity } from "../../Redux/Actions/leadActions";
import { viewCountryListPagination, viewAllCity } from "../../Redux/Actions/settingsActions";
import { agentProfitRangeList } from "../../Redux/Actions/agentProfitRangeActions";
import { getProjectList } from "../../Redux/Actions/projectActions";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import SearchableMultiSelect from "../../Global Functions/SearchableMultiSelect";
import toast from "react-hot-toast";
import "./addLead.css";

const libraries = ["places"];

const mapContainerStyle = {
    width: "100%",
    height: "300px",
    borderRadius: "10px",
    marginTop: "10px",
};

const defaultCenter = { lat: 28.6139, lng: 77.209 };

const SITE_VISIT_BY = [
    { value: "self", label: "Self" },
    { value: "byagent", label: "By Agent" },
];

const LEAD_SOURCES = [
    { value: "other", label: "Other" },
    { value: "partner", label: "Partner" },
    { value: "meta_ads", label: "Meta Ads" },
    { value: "google_ads", label: "Google Ads" },
    { value: "user_app", label: "User App" },
];

const LEAD_FOR = [
    { value: "home_visit", label: "Home Visit" },
    { value: "Site_visit", label: "Site Visit" },
];

const emptyAddress = {
    address: "",
    appartment_road_area: "",
    business_type: "",
    direction_to_reach: "",
    house_flat_floorno: "",
};

const formatTimeTo12Hour = (time24) => {
    if (!time24) return "";
    let [hour, minute] = time24.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
};

const formatTimeTo24Hour = (time12) => {
    const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec((time12 || "").trim());
    if (!m) return "";
    let h = Number(m[1]) % 12;
    if (m[3].toUpperCase() === "PM") h += 12;
    return `${String(h).padStart(2, "0")}:${m[2]}`;
};

const getMinAppointmentDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
};

const EditLead = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const autocompleteRef = useRef(null);

    const { countryList } = useSelector((state) => state.country);
    const { citiesList } = useSelector((state) => state.city || {});
    const { agentProfitRanges } = useSelector((state) => state.agentProfitRange || {});
    const { localityList } = useSelector((state) => state.leads || {});
    const { projects: projectListData } = useSelector((state) => state.projects || {});

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries,
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [pageLoading, setPageLoading] = useState(true);
    const [noteInput, setNoteInput] = useState("");
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [markerPosition, setMarkerPosition] = useState(null);

    const [leadData, setLeadData] = useState({
        name: "",
        email: "",
        country_code: "+91",
        contact_number: "",
        country: "",
        city: "",
        locality: "",
        project_id: [],
        address: { ...emptyAddress },
        lat: 0,
        lng: 0,
        price_range: "",
        lead_source: "partner",
        appointment_date: "",
        slot_start_time: "",
        slot_end_time: "",
        lead_for: "home_visit",
        site_visit_by: "self",
        site_visit_purpose: "",
        note: [],
    });

    const isSiteVisit = leadData.lead_for === "Site_visit";

    useEffect(() => {
        dispatch(viewCountryListPagination());
        dispatch(viewAllCity());
        dispatch(agentProfitRangeList({ page: 1, limit: 100 }));
    }, [dispatch]);

    // Prefill from existing lead
    useEffect(() => {
        const fetchLead = async () => {
            try {
                const res = await dispatch(viewLeadById(id));
                if (res?.statusCode !== 200) return;
                const l = res.data;

                // if (l.lead_source !== "partner" || !l.partner) {
                //     toast.error("Only partner leads can be updated");
                //     return navigate(`/leads/view/${id}`);
                // }

                const coords = l.location?.coordinates;
                const hasCoords = coords?.length === 2 && (coords[0] !== 0 || coords[1] !== 0);
                const cityId = l.city?._id || l.city || "";
                const siteVisit = l.lead_for === "Site_visit";

                let startTime = "", endTime = "", date = "";
                const [s, e] = (l.time_slot || "").split(" to ");
                endTime = formatTimeTo24Hour(e);
                if (siteVisit && l.pickup_date_time) {
                    const d = new Date(l.pickup_date_time);
                    date = d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                    startTime = d.toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
                } else {
                    date = l.appointment_date ? l.appointment_date.slice(0, 10) : "";
                    startTime = formatTimeTo24Hour(s);
                }

                setLeadData({
                    name: l.name || "",
                    email: l.email || "",
                    country_code: (l.country_code || "").trim() || "+91",
                    contact_number: (l.contact_number || "").trim(),
                    country: l.country?._id || l.country || "",
                    city: cityId,
                    locality: l.locality?._id || l.locality || "",
                    project_id: (l.project_id || []).map((p) => p._id || p),
                    address: { ...emptyAddress, ...(l.address || {}) },
                    lat: hasCoords ? coords[1] : 0,
                    lng: hasCoords ? coords[0] : 0,
                    price_range: l.price_range?._id || l.price_range || "",
                    lead_source: l.lead_source || "partner",
                    appointment_date: date,
                    slot_start_time: startTime,
                    slot_end_time: endTime,
                    lead_for: l.lead_for || "home_visit",
                    site_visit_by: l.site_visit_by || "self",
                    site_visit_purpose: l.site_visit_purpose || "",
                    note: l.note || [],
                });

                if (hasCoords) {
                    setMarkerPosition({ lat: coords[1], lng: coords[0] });
                    setMapCenter({ lat: coords[1], lng: coords[0] });
                }
                if (cityId) dispatch(listLocalityByCity({ city_id: cityId }));
            } finally {
                setPageLoading(false);
            }
        };
        fetchLead();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!leadData.city || !leadData.locality) return;
        dispatch(getProjectList({
            page: 1,
            limit: 100,
            is_deleted: false,
            city: leadData.city,
            localities: [leadData.locality],
            status: ["published"],
        }, false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadData.city, leadData.locality]);

    const clearError = (field) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "city") {
            setLeadData((prev) => ({ ...prev, city: value, locality: "", project_id: [] }));
            if (value) dispatch(listLocalityByCity({ city_id: value }));
        } else if (name === "locality") {
            setLeadData((prev) => ({ ...prev, locality: value, project_id: [] }));
        } else {
            setLeadData((prev) => ({ ...prev, [name]: value }));
        }
        clearError(name);
    };

    const handleProjectChange = (values) => {
        setLeadData((prev) => ({ ...prev, project_id: values }));
        clearError("project_id");
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setLeadData((prev) => ({ ...prev, address: { ...prev.address, [name]: value } }));
        if (name === "address") clearError("address");
    };

    const onAutocompleteLoad = (ac) => {
        autocompleteRef.current = ac;
    };

    const onPlaceChanged = () => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setMarkerPosition({ lat, lng });
            setMapCenter({ lat, lng });
            setLeadData((prev) => ({
                ...prev,
                address: { ...prev.address, address: place.formatted_address || prev.address.address },
                lat,
                lng,
            }));
            clearError("address");
        }
    };

    const onMapClick = useCallback((event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });
        setLeadData((prev) => ({ ...prev, lat, lng }));
        if (window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === "OK" && results[0]) {
                    setLeadData((prev) => ({
                        ...prev,
                        address: { ...prev.address, address: results[0].formatted_address },
                    }));
                    clearError("address");
                }
            });
        }
    }, []);

    const handleAddNote = () => {
        const val = noteInput.trim();
        if (!val) return;
        setLeadData((prev) => ({ ...prev, note: [...prev.note, val] }));
        setNoteInput("");
    };

    const handleNoteKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddNote();
        }
    };

    const handleRemoveNote = (idx) => {
        setLeadData((prev) => ({ ...prev, note: prev.note.filter((_, i) => i !== idx) }));
    };

    const validate = () => {
        const newErrors = {};
        if (!leadData.name.trim()) newErrors.name = "Name is required";
        if (!leadData.contact_number.trim()) newErrors.contact_number = "Contact number is required";
        if (!leadData.lead_source) newErrors.lead_source = "Lead source is required";
        if (!leadData.country) newErrors.country = "Country is required";
        if (!leadData.city) newErrors.city = "City is required";
        if (!isSiteVisit && !leadData.address.address.trim()) newErrors.address = "Address is required";
        if (!leadData.locality) newErrors.locality = "Locality is required";
        if (isSiteVisit && !leadData.project_id.length) newErrors.project_id = "At least one project is required";
        if (!leadData.price_range) newErrors.price_range = "Price range is required";
        if (!leadData.appointment_date) newErrors.appointment_date = "Appointment date is required";
        if (!leadData.slot_start_time) newErrors.slot_start_time = "Start time is required";
        if (!leadData.slot_end_time) newErrors.slot_end_time = "End time is required";
        if (
            leadData.slot_start_time &&
            leadData.slot_end_time &&
            leadData.slot_start_time >= leadData.slot_end_time
        ) newErrors.slot_end_time = "End time must be after start time";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        try {
            setLoading(true);

            const payload = {
                lead_id: id,
                name: leadData.name,
                country_code: leadData.country_code,
                contact_number: leadData.contact_number,
                lead_source: leadData.lead_source,
                lead_for: leadData.lead_for,
                country: leadData.country,
                city: leadData.city,
                address: leadData.address,
                price_range: leadData.price_range,
                locality: leadData.locality,
                project_id: leadData.project_id,
                appointment_date: leadData.appointment_date,
                time_slot: `${formatTimeTo12Hour(leadData.slot_start_time)} to ${formatTimeTo12Hour(leadData.slot_end_time)}`,
            };

            if (leadData.email) payload.email = leadData.email;
            if (leadData.note.length) payload.note = leadData.note;

            if (leadData.lat && leadData.lng) {
                payload.location = {
                    type: "Point",
                    coordinates: [leadData.lng, leadData.lat],
                };
            }

            if (isSiteVisit) {
                payload.pickup_address = leadData.address.address;
                payload.pickup_date_time = new Date(`${leadData.appointment_date}T${leadData.slot_start_time}`);
                payload.site_visit_by = leadData.site_visit_by;
                if (leadData.site_visit_purpose) payload.site_visit_purpose = leadData.site_visit_purpose;
            }

            const response = await dispatch(updateLead(payload));

            if (response?.statusCode === 200) {
                navigate(`/leads/view/${id}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const formatIndianPrice = (value) => {
        if (value >= 10000000) return `₹${(value / 10000000).toFixed(value % 10000000 === 0 ? 0 : 2)}Cr`;
        if (value >= 100000) return `₹${(value / 100000).toFixed(value % 100000 === 0 ? 0 : 2)}L`;
        return `₹${value.toLocaleString("en-IN")}`;
    };

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="al-wrapper"><p>Loading lead data…</p></div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="al-wrapper">
                <div className="al-header">
                    <div className="al-header-left">
                        <div>
                            <h1 className="al-title">Update Lead Details</h1>
                            <p className="al-subtitle">Add missing details for this partner lead</p>
                        </div>
                    </div>
                    <Link to={`/leads/view/${id}`} className="al-view-btn">Back to Lead</Link>
                </div>

                <form className="al-form" onSubmit={handleSubmit}>
                    {/* ── Section 00: Lead For ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">00</span>
                            <h2 className="al-section-title">Lead For</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Lead For <span className="al-req">*</span></label>
                                <select
                                    className="al-select"
                                    name="lead_for"
                                    value={leadData.lead_for}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    {LEAD_FOR.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>

                            {isSiteVisit && (
                                <div className="al-field">
                                    <label className="al-label">Visit By <span className="al-req">*</span></label>
                                    <select
                                        className="al-select"
                                        name="site_visit_by"
                                        value={leadData.site_visit_by}
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        {SITE_VISIT_BY.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Section 01: Basic Info ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">01</span>
                            <h2 className="al-section-title">Basic Information</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Name <span className="al-req">*</span></label>
                                <input
                                    className={`al-input ${errors.name ? "al-input--error" : ""}`}
                                    type="text"
                                    name="name"
                                    value={leadData.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Rahul Sharma"
                                    disabled={loading}
                                />
                                {errors.name && <span className="al-error-msg">{errors.name}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">Email <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="email"
                                    name="email"
                                    value={leadData.email}
                                    onChange={handleChange}
                                    placeholder="e.g. rahul@example.com"
                                    disabled={loading}
                                />
                            </div>

                            <div className="al-field">
                                <label className="al-label">Country Code</label>
                                <select
                                    className="al-select"
                                    name="country_code"
                                    value={leadData.country_code}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="+91">India (+91)</option>
                                    <option value="+1">USA (+1)</option>
                                    <option value="+971">UAE (+971)</option>
                                </select>
                            </div>

                            <div className="al-field">
                                <label className="al-label">Contact Number <span className="al-req">*</span></label>
                                <input
                                    className={`al-input ${errors.contact_number ? "al-input--error" : ""}`}
                                    type="text"
                                    name="contact_number"
                                    value={leadData.contact_number}
                                    onChange={handleChange}
                                    placeholder="e.g. 9876543211"
                                    disabled={loading}
                                />
                                {errors.contact_number && <span className="al-error-msg">{errors.contact_number}</span>}
                            </div>

                            {isSiteVisit && (
                                <div className="al-field">
                                    <label className="al-label">Site Visit Purpose <span className="al-optional">(Optional)</span></label>
                                    <input
                                        className="al-input"
                                        type="text"
                                        name="site_visit_purpose"
                                        value={leadData.site_visit_purpose}
                                        onChange={handleChange}
                                        placeholder="e.g. Property inspection"
                                        disabled={loading}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Section 02: Location ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">02</span>
                            <h2 className="al-section-title">Location</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Country <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.country ? "al-input--error" : ""}`}
                                    name="country"
                                    value={leadData.country}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select country</option>
                                    {countryList?.data?.map((country) => (
                                        <option key={country._id} value={country._id}>{country.country_name}</option>
                                    ))}
                                </select>
                                {errors.country && <span className="al-error-msg">{errors.country}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">City <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.city ? "al-input--error" : ""}`}
                                    name="city"
                                    value={leadData.city}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select city</option>
                                    {citiesList?.data?.map((city) => (
                                        <option key={city._id} value={city._id}>{city.city_name}</option>
                                    ))}
                                </select>
                                {errors.city && <span className="al-error-msg">{errors.city}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">Locality <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.locality ? "al-input--error" : ""}`}
                                    name="locality"
                                    value={leadData.locality}
                                    onChange={handleChange}
                                    disabled={!leadData.city || loading}
                                >
                                    <option value="">{leadData.city ? "Select locality" : "Select city first"}</option>
                                    {(localityList?.data || []).map((loc) => (
                                        <option key={loc._id} value={loc._id}>{loc.name}</option>
                                    ))}
                                </select>
                                {errors.locality && <span className="al-error-msg">{errors.locality}</span>}
                            </div>

                            <SearchableMultiSelect
                                label="Project"
                                required={isSiteVisit}
                                placeholder={!leadData.locality ? "Select locality first" : "Search projects..."}
                                disabled={!leadData.locality || loading}
                                options={(projectListData?.data || []).map((p) => ({
                                    value: p._id,
                                    label: p.project_name,
                                }))}
                                selected={leadData.project_id}
                                onChange={handleProjectChange}
                                error={errors.project_id}
                                emptyMessage="Select a locality first"
                            />

                            <div className="al-field al-field--full">
                                <label className="al-label">
                                    {isSiteVisit ? "Pickup Address" : "Address"}
                                    {!isSiteVisit && <span className="al-req">*</span>}
                                </label>

                                {loadError && (
                                    <div className="al-map-placeholder">
                                        <p>Map unavailable — check your Google Maps API key</p>
                                    </div>
                                )}

                                {!isLoaded && !loadError && (
                                    <div className="al-map-placeholder">
                                        <p>Loading map...</p>
                                    </div>
                                )}

                                {isLoaded && !loadError && (
                                    <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                                        <input
                                            className={`al-input ${errors.address ? "al-input--error" : ""}`}
                                            type="text"
                                            name="address"
                                            value={leadData.address.address}
                                            onChange={handleAddressChange}
                                            placeholder={isSiteVisit ? "Search pickup location..." : "Search area or address..."}
                                            disabled={loading}
                                        />
                                    </Autocomplete>
                                )}

                                {errors.address && <span className="al-error-msg">{errors.address}</span>}

                                {isLoaded && !loadError && (
                                    <>
                                        <GoogleMap
                                            mapContainerStyle={mapContainerStyle}
                                            center={mapCenter}
                                            zoom={13}
                                            onClick={onMapClick}
                                        >
                                            {markerPosition && <Marker position={markerPosition} />}
                                        </GoogleMap>
                                        <p className="al-map-hint">Click on the map or search above to set the location</p>
                                    </>
                                )}
                            </div>

                            <div className="al-field">
                                <label className="al-label">House / Flat / Floor No. <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="text"
                                    name="house_flat_floorno"
                                    value={leadData.address.house_flat_floorno}
                                    onChange={handleAddressChange}
                                    placeholder="e.g. #6676"
                                    disabled={loading}
                                />
                            </div>

                            <div className="al-field">
                                <label className="al-label">Apartment / Road / Area <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="text"
                                    name="appartment_road_area"
                                    value={leadData.address.appartment_road_area}
                                    onChange={handleAddressChange}
                                    placeholder="e.g. Great India Place, Bhangel, Secor-18, Noida"
                                    disabled={loading}
                                />
                            </div>

                            <div className="al-field">
                                <label className="al-label">Direction to Reach <span className="al-optional">(Optional)</span></label>
                                <input
                                    className="al-input"
                                    type="text"
                                    name="direction_to_reach"
                                    value={leadData.address.direction_to_reach}
                                    onChange={handleAddressChange}
                                    placeholder="e.g. Near main gate"
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Section 03: Lead Details ── */}
                    <div className="al-section">
                        <div className="al-section-header">
                            <span className="al-section-badge">03</span>
                            <h2 className="al-section-title">Lead Details</h2>
                        </div>

                        <div className="al-grid al-grid-2">
                            <div className="al-field">
                                <label className="al-label">Lead Source <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.lead_source ? "al-input--error" : ""}`}
                                    name="lead_source"
                                    value={leadData.lead_source}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    {LEAD_SOURCES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                                {errors.lead_source && <span className="al-error-msg">{errors.lead_source}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">Price Range <span className="al-req">*</span></label>
                                <select
                                    className={`al-select ${errors.price_range ? "al-input--error" : ""}`}
                                    name="price_range"
                                    value={leadData.price_range}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select price range</option>
                                    {Array.isArray(agentProfitRanges) && agentProfitRanges.map((r) => (
                                        <option key={r._id} value={r._id}>
                                            {r.category} — {formatIndianPrice(r.min_price)} to {formatIndianPrice(r.max_price)}
                                        </option>
                                    ))}
                                </select>
                                {errors.price_range && <span className="al-error-msg">{errors.price_range}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">
                                    {isSiteVisit ? "Pickup Date" : "Appointment Date"} <span className="al-req">*</span>
                                </label>
                                <input
                                    className={`al-input ${errors.appointment_date ? "al-input--error" : ""}`}
                                    type="date"
                                    name="appointment_date"
                                    min={getMinAppointmentDate()}
                                    value={leadData.appointment_date}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                {errors.appointment_date && <span className="al-error-msg">{errors.appointment_date}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">
                                    {isSiteVisit ? "Pickup Time" : "Slot Start Time"} <span className="al-req">*</span>
                                </label>
                                <input
                                    className={`al-input ${errors.slot_start_time ? "al-input--error" : ""}`}
                                    type="time"
                                    name="slot_start_time"
                                    min="09:00"
                                    max="19:00"
                                    value={leadData.slot_start_time}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                {errors.slot_start_time && <span className="al-error-msg">{errors.slot_start_time}</span>}
                            </div>

                            <div className="al-field">
                                <label className="al-label">Slot End Time <span className="al-req">*</span></label>
                                <input
                                    className={`al-input ${errors.slot_end_time ? "al-input--error" : ""}`}
                                    type="time"
                                    name="slot_end_time"
                                    min="09:00"
                                    max="19:00"
                                    value={leadData.slot_end_time}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                {errors.slot_end_time && <span className="al-error-msg">{errors.slot_end_time}</span>}
                            </div>

                            <div className="al-field al-field--full">
                                <label className="al-label">Notes <span className="al-optional">(Optional)</span></label>
                                <div className="al-note-input-wrap">
                                    <input
                                        className="al-input"
                                        type="text"
                                        value={noteInput}
                                        onChange={(e) => setNoteInput(e.target.value)}
                                        onKeyDown={handleNoteKeyDown}
                                        placeholder="Type a note and press Enter"
                                        disabled={loading}
                                    />
                                    <button type="button" className="al-note-add-btn" onClick={handleAddNote} disabled={loading}>
                                        Add
                                    </button>
                                </div>
                                {leadData.note.length > 0 && (
                                    <div className="al-note-chips">
                                        {leadData.note.map((n, idx) => (
                                            <span key={idx} className="al-note-chip">
                                                {n}
                                                <button
                                                    type="button"
                                                    className="al-note-chip-remove"
                                                    onClick={() => handleRemoveNote(idx)}
                                                    disabled={loading}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Footer ── */}
                    <div className="al-footer">
                        <Link to={`/leads/view/${id}`} className="al-btn al-btn--cancel">Cancel</Link>
                        <button type="submit" className="al-btn al-btn--submit" disabled={loading}>
                            {loading ? "Saving..." : "Update Lead"}
                        </button>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default EditLead;